// src/components/admin/scheduling/schedulingApi.js
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  doc,
  updateDoc,
  addDoc,
  orderBy,
  limit,
  getDoc,
  documentId,
  setDoc,
  writeBatch,
  deleteDoc,
  serverTimestamp,
  increment,
  onSnapshot,
} from "firebase/firestore";

import { db } from "../../../firebaseConfig"; 
import { logActivity } from "./loggingApi";
import { getAuth } from "firebase/auth";
import { isRejectedStatus } from "../../../utils/userStatus";
import { readDeveloperModeFlag } from "./developerModeState";
import { resolveCopiedStudentGrade } from "./sessionCopyUtils";
import { calculateBusinessRoundedHoursFromMinutes } from "../../../utils/sessionHours";
import {
  buildRegularScheduleTypeDraft,
  normalizeDaySchedule,
  normalizeWeekConfig,
  LEGACY_REGULAR_WEEK_CONFIG,
} from "./scheduleSettingsUtils";

const USERS_COL = "users";
const STUDENTS_COL = "students";
const AVAIL_COL = "tutor_availability_blocks";
const SUBJECTS_COL = "subjects";
const CLOCK_EVENTS_COL = "clock_events";
const ACTIVE_SESSIONS_COL = "active_sessions";

export const SESSIONS_COL = "scheduled_sessions";
const DAYCFG_COL = "schedule_day_configs";
const SETTINGS_COL = "settings";
const SCHEDULE_TYPES_COL = "schedule_types";
const SCHEDULE_APPLICATIONS_COL = "schedule_applications";

const MASTER_SCHEDULE_SESSIONS_COL = "master_schedule_sessions";

function chunkArray(input = [], chunkSize = 450) {
  const out = [];
  for (let i = 0; i < input.length; i += chunkSize) {
    out.push(input.slice(i, i + chunkSize));
  }
  return out;
}

function enumerateDatesInclusive(startDate, endDate) {
  const out = [];
  const cur = new Date(startDate);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  while (cur <= end) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// --- HELPERS FOR LOGGING & DATES ---

let cachedAdminName = null; // Add this variable to store the name once fetched

async function fetchAdminName() {
  // 1. Return cached name if we already found it
  if (cachedAdminName) return cachedAdminName;

  const user = getAuth().currentUser;
  if (!user) return "Unknown Admin";

  // 2. Check Firestore 'users' collection for the real name
  try {
    // We assume USERS_COL is "users", defined at top of file
    const userDocRef = doc(db, USERS_COL, user.uid);
    const userSnap = await getDoc(userDocRef);
    
    if (userSnap.exists() && userSnap.data().name) {
      cachedAdminName = userSnap.data().name;
      return cachedAdminName;
    }
  } catch (error) {
    console.warn("Failed to fetch admin name from Firestore:", error);
  }

  // 3. Fallback: Use Auth display name or email if Firestore lookup fails
  cachedAdminName = user.displayName || user.email;
  return cachedAdminName;
}

async function logSchedulingActivity(actionType, description, meta = {}) {
  if (readDeveloperModeFlag()) return;
  await logActivity(actionType, description, meta);
}

function toDateMaybe(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts.toDate === "function") return ts.toDate();
  return null;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const asNum = Number(value);
  return Number.isFinite(asNum) ? asNum : 0;
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function isScheduleDayOverrideRecord(row) {
  if (!row) return false;
  if (row.source === "schedule_day_settings") return true;
  if (typeof row.id === "string" && row.id.startsWith("day_")) return true;
  if (hasOwn(row, "unavailable") || hasOwn(row, "hidden")) return true;
  return false;
}

function shouldReplaceAvailabilityRecord(current, candidate) {
  if (!current) return true;

  const currentIsOverride = isScheduleDayOverrideRecord(current);
  const candidateIsOverride = isScheduleDayOverrideRecord(candidate);

  if (candidateIsOverride !== currentIsOverride) {
    return candidateIsOverride;
  }

  const currentUpdated = timestampToMillis(current?.updatedAt);
  const candidateUpdated = timestampToMillis(candidate?.updatedAt);
  if (candidateUpdated !== currentUpdated) {
    return candidateUpdated > currentUpdated;
  }

  const currentStart = toDateMaybe(current?.start)?.getTime() || 0;
  const candidateStart = toDateMaybe(candidate?.start)?.getTime() || 0;
  return candidateStart > currentStart;
}

function formatTimeOnly(dateObj) {
  if (!dateObj) return "???";
  const d = toDateMaybe(dateObj);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatRange(start, end) {
  return `${formatTimeOnly(start)} - ${formatTimeOnly(end)}`;
}

function normalizeSessionStatus(status) {
  const value = String(status || "Pending").trim().toLowerCase();
  if (value === "cancelled" || value === "canceled") return "canceled";
  if (value === "confirmed") return "confirmed";
  if (value === "present") return "present";
  if (value === "no show" || value === "noshow" || value === "no_show" || value === "no-show") return "no_show";
  return "pending";
}

function calculateRoundedUsedHours(start, end) {
  const s = toDateMaybe(start);
  const e = toDateMaybe(end);
  if (!s || !e) return 0;
  const diffMin = Math.ceil((e.getTime() - s.getTime()) / 60000);
  if (diffMin <= 0) return 0;
  return calculateBusinessRoundedHoursFromMinutes(diffMin);
}

// Generates: "Monday, 1/19/2026 (4:00 PM - 5:00 PM)"
function formatLogContext(dateKey, start, end) {
  const d = toDateMaybe(start);
  if (!d) return dateKey || "Unknown Date";

  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = d.toLocaleDateString('en-US'); // 1/19/2026
  const timeStr = formatRange(start, end);
  
  return `${weekday}, ${dateStr} (${timeStr})`;
}

// --- FETCHERS (READ OPERATIONS) ---

export async function fetchActiveTutors() {
  // Fetch all users to avoid Firestore's "only one 'in' clause" query limitation
  const snap = await getDocs(collection(db, USERS_COL));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Filter in Javascript: Keep tutors/admins, but never include rejected accounts
  return rows.filter(r => {
    const role = String(r.role || "").toLowerCase();
    if (isRejectedStatus(r.status)) return false;
    return role === "tutor" || role === "admin";
  });
}

export async function fetchAllStudents() {
  const q = query(
    collection(db, STUDENTS_COL), 
    orderBy("name", "asc") 
  );
  
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchStudentsByIds(studentIds = []) {
  const ids = [...new Set((studentIds || []).filter(Boolean))];
  if (!ids.length) return [];

  const rows = [];
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const q = query(collection(db, STUDENTS_COL), where(documentId(), "in", chunk));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => rows.push({ id: d.id, ...d.data() }));
  }
  return rows;
}

export async function fetchAvailabilityBlocks({ rangeStart, rangeEnd, tutorUid = null }) {
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);

  let q = query(
    collection(db, AVAIL_COL),
    where("start", ">=", startTs),
    where("start", "<", endTs)
  );

  if (tutorUid) q = query(q, where("uid", "==", tutorUid));

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    start: toDateMaybe(d.data().start),
    end: toDateMaybe(d.data().end),
  }));

  const byTutorDay = new Map();
  for (const row of rows) {
    const uid = row?.uid;
    const start = toDateMaybe(row?.start);
    if (!uid || !start) continue;

    const key = `${uid}|${dateKeyLocal(start)}`;
    const current = byTutorDay.get(key);
    if (shouldReplaceAvailabilityRecord(current, row)) {
      byTutorDay.set(key, row);
    }
  }

  return [...byTutorDay.values()].sort((a, b) => {
    const uidA = String(a?.uid || "");
    const uidB = String(b?.uid || "");
    if (uidA !== uidB) return uidA.localeCompare(uidB);
    return (toDateMaybe(a?.start)?.getTime() || 0) - (toDateMaybe(b?.start)?.getTime() || 0);
  });
}

export async function fetchScheduledSessions({ rangeStart, rangeEnd, tutorUid = null, includeDeleted = false }) {
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);

  let q = query(
    collection(db, SESSIONS_COL),
    where("start", ">=", startTs),
    where("start", "<", endTs)
  );

  if (tutorUid) q = query(q, where("tutorUid", "==", tutorUid));

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    start: toDateMaybe(d.data().start),
    end: toDateMaybe(d.data().end),
  }));

  if (includeDeleted) return rows;
  return rows.filter((s) => !s?.isDeleted);
}

function clipIntervalToRange(start, end, rangeStart, rangeEnd) {
  if (!start || !end || end <= start) return null;
  if (end <= rangeStart || start >= rangeEnd) return null;

  const clippedStart = start < rangeStart ? rangeStart : start;
  const clippedEnd = end > rangeEnd ? rangeEnd : end;
  if (clippedEnd <= clippedStart) return null;

  return { start: clippedStart, end: clippedEnd };
}

export async function fetchTutorClockIntervals({ rangeStart, rangeEnd, tutorUid = null }) {
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);
  const now = new Date();

  // Keep tutor query index-light (uid-only) to avoid composite-index churn.
  // Admin view can safely use date-range query.
  const closedQuery = tutorUid
    ? query(collection(db, CLOCK_EVENTS_COL), where("uid", "==", tutorUid))
    : query(
      collection(db, CLOCK_EVENTS_COL),
      where("clockOutAt", ">=", startTs),
      where("clockOutAt", "<", endTs)
    );

  let activeQuery = collection(db, ACTIVE_SESSIONS_COL);
  if (tutorUid) {
    activeQuery = query(activeQuery, where("uid", "==", tutorUid));
  }

  const [closedSnap, activeSnap] = await Promise.all([getDocs(closedQuery), getDocs(activeQuery)]);

  const intervals = [];

  for (const d of closedSnap.docs) {
    const data = d.data() || {};
    if ((data.type || "").toString() !== "clock_out") continue;
    if (tutorUid && (data.uid || "").toString() !== tutorUid) continue;

    const start = toDateMaybe(data.clockInAt) || toDateMaybe(data.startTime) || toDateMaybe(data.time);
    const end = toDateMaybe(data.clockOutAt);
    const clipped = clipIntervalToRange(start, end, rangeStart, rangeEnd);
    if (!clipped) continue;

    intervals.push({
      id: d.id,
      docId: d.id,
      uid: (data.uid || "").toString(),
      start: clipped.start,
      end: clipped.end,
      isOpen: false,
      source: (data.source || "kiosk_clock").toString(),
    });
  }

  for (const d of activeSnap.docs) {
    const data = d.data() || {};
    const start = toDateMaybe(data.startTime) || toDateMaybe(data.clockInAt) || toDateMaybe(data.time);
    const clipped = clipIntervalToRange(start, now, rangeStart, rangeEnd);
    if (!clipped) continue;

    intervals.push({
      id: `active_${d.id}`,
      docId: d.id,
      uid: (data.uid || d.id || "").toString(),
      start: clipped.start,
      end: clipped.end,
      isOpen: true,
      source: (data.source || "kiosk_clock").toString(),
    });
  }

  return intervals.sort((a, b) => {
    const uidA = String(a.uid || "");
    const uidB = String(b.uid || "");
    if (uidA !== uidB) return uidA.localeCompare(uidB);
    return (a.start?.getTime?.() || 0) - (b.start?.getTime?.() || 0);
  });
}

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function formatPayrollDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function mergeTimeRanges(ranges = []) {
  const sorted = [...(ranges || [])]
    .filter((range) => range?.start instanceof Date && range?.end instanceof Date && range.end > range.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  if (!sorted.length) return [];
  const merged = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.start <= last.end) {
      if (current.end > last.end) last.end = current.end;
      continue;
    }
    merged.push({ ...current });
  }

  return merged;
}

function splitShiftIntoPayrollSegments({
  shiftStart,
  shiftEnd,
  teachingWindows,
  adminRate,
  teachingRate,
}) {
  if (!(shiftStart instanceof Date) || !(shiftEnd instanceof Date) || shiftEnd <= shiftStart) return [];

  const mergedTeaching = mergeTimeRanges(
    (teachingWindows || []).map((window) => ({
      start: window.start < shiftStart ? shiftStart : window.start,
      end: window.end > shiftEnd ? shiftEnd : window.end,
    }))
  );

  const segments = [];
  let cursor = shiftStart;

  for (const window of mergedTeaching) {
    if (window.start > cursor) {
      segments.push({ type: "admin", start: new Date(cursor), end: new Date(window.start) });
    }
    const teachStart = window.start > cursor ? window.start : cursor;
    const teachEnd = window.end;
    if (teachEnd > teachStart) {
      segments.push({ type: "teaching", start: new Date(teachStart), end: new Date(teachEnd) });
      cursor = teachEnd;
    }
  }

  if (cursor < shiftEnd) {
    segments.push({ type: "admin", start: new Date(cursor), end: new Date(shiftEnd) });
  }

  return segments
    .filter((segment) => segment.end > segment.start)
    .map((segment) => {
      const hours = round2((segment.end.getTime() - segment.start.getTime()) / 3600000);
      const rateUsed = segment.type === "teaching" ? Number(teachingRate) || 0 : Number(adminRate) || 0;
      return {
        ...segment,
        hours,
        rateUsed,
        totalPay: round2(hours * rateUsed),
      };
    });
}

async function fetchTeachingWindowsForShift({ uid, shiftStart, shiftEnd, forceAdminOnly = false }) {
  if (forceAdminOnly) return [];

  const snap = await getDocs(
    query(
      collection(db, SESSIONS_COL),
      where("tutorUid", "==", uid)
    )
  );

  return snap.docs
    .map((d) => d.data() || {})
    .filter((row) => !row?.isDeleted)
    .filter((row) => {
      const status = (row?.status || "").toString().toLowerCase();
      return status !== "cancelled" && status !== "canceled";
    })
    .map((row) => ({
      start: toDateMaybe(row.start),
      end: toDateMaybe(row.end),
    }))
    .filter((window) => window.start instanceof Date && window.start <= shiftEnd && window.end >= shiftStart)
    .filter((window) => window.start instanceof Date && window.end instanceof Date && window.end > window.start)
    .map((window) => ({
      start: window.start < shiftStart ? shiftStart : window.start,
      end: window.end > shiftEnd ? shiftEnd : window.end,
    }))
    .filter((window) => window.end > window.start);
}

export async function adjustClockInterval({
  interval,
  nextClockInAt,
  nextClockOutAt = null,
  editorUid = null,
  editorEmail = null,
}) {
  const nextStart = toDateMaybe(nextClockInAt);
  const nextEnd = toDateMaybe(nextClockOutAt);

  if (!(nextStart instanceof Date)) {
    throw new Error("Clock In time is invalid.");
  }

  const uid = (interval?.uid || "").toString();
  if (!uid) {
    throw new Error("Clock interval is missing uid.");
  }

  const openRef = doc(db, "open_clock_sessions", uid);
  const legacyRef = doc(db, ACTIVE_SESSIONS_COL, uid);

  if (interval?.isOpen) {
    const activeDocId = (interval?.docId || uid || "").toString();
    if (!activeDocId) {
      throw new Error("Open clock interval is missing doc id.");
    }

    const batch = writeBatch(db);
    const activeRef = doc(db, ACTIVE_SESSIONS_COL, activeDocId);

    batch.set(
      activeRef,
      {
        startTime: Timestamp.fromDate(nextStart),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    batch.set(
      openRef,
      {
        clockInAt: Timestamp.fromDate(nextStart),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    batch.set(doc(collection(db, CLOCK_EVENTS_COL)), {
      uid,
      type: "clock_adjusted_open",
      source: "admin_secret_clock_edit",
      adjustedClockInAt: Timestamp.fromDate(nextStart),
      editedByUid: editorUid || null,
      editedByEmail: editorEmail || null,
      createdAt: serverTimestamp(),
    });

    await batch.commit();
    return { adjustedSegments: 0, adjustedPayrollRecordIds: [] };
  }

  if (!(nextEnd instanceof Date) || nextEnd <= nextStart) {
    throw new Error("Clock Out must be after Clock In.");
  }

  const eventId = (interval?.docId || interval?.id || "").toString();
  if (!eventId) {
    throw new Error("Closed clock interval is missing event id.");
  }

  const eventRef = doc(db, CLOCK_EVENTS_COL, eventId);
  const eventSnap = await getDoc(eventRef);
  if (!eventSnap.exists()) {
    throw new Error("Clock event no longer exists.");
  }

  const eventData = eventSnap.data() || {};
  const payrollRecordIds = Array.isArray(eventData.payrollRecordIds)
    ? eventData.payrollRecordIds.filter(Boolean).map((id) => String(id))
    : [];

  const userSnap = await getDoc(doc(db, USERS_COL, uid));
  const userData = userSnap.exists() ? userSnap.data() || {} : {};
  const userRole = String(userData?.role || "").toLowerCase();
  const tutorName = String(userData?.name || eventData?.userName || interval?.userName || uid);
  const adminRate = Number(userData?.adminRate) || 0;
  const teachingRate = Number(userData?.teachingRate) || 0;

  const teachingWindows = await fetchTeachingWindowsForShift({
    uid,
    shiftStart: nextStart,
    shiftEnd: nextEnd,
    forceAdminOnly: userRole === "admin",
  });

  const segments = splitShiftIntoPayrollSegments({
    shiftStart: nextStart,
    shiftEnd: nextEnd,
    teachingWindows,
    adminRate,
    teachingRate,
  });

  const batch = writeBatch(db);

  for (const payrollId of payrollRecordIds) {
    batch.delete(doc(db, "payroll_records", payrollId));
  }

  const nextPayrollIds = [];
  for (const segment of segments) {
    const payrollRef = doc(collection(db, "payroll_records"));
    nextPayrollIds.push(payrollRef.id);
    batch.set(payrollRef, {
      uid,
      tutorName,
      date: formatPayrollDate(segment.start),
      type: segment.type,
      startTime: Timestamp.fromDate(segment.start),
      endTime: Timestamp.fromDate(segment.end),
      hours: segment.hours,
      rateUsed: segment.rateUsed,
      totalPay: segment.totalPay,
      status: "approved",
      source: "kiosk_clock",
      clockSessionId: uid,
      createdAt: serverTimestamp(),
      adjustedByUid: editorUid || null,
      adjustedByEmail: editorEmail || null,
    });
  }

  const totalHours = round2(segments.reduce((acc, seg) => acc + (Number(seg.hours) || 0), 0));
  const totalPay = round2(segments.reduce((acc, seg) => acc + (Number(seg.totalPay) || 0), 0));

  batch.set(
    eventRef,
    {
      clockInAt: Timestamp.fromDate(nextStart),
      clockOutAt: Timestamp.fromDate(nextEnd),
      payrollRecordIds: nextPayrollIds,
      segmentCount: segments.length,
      editedByUid: editorUid || null,
      editedByEmail: editorEmail || null,
      editedAt: serverTimestamp(),
      source: eventData?.source || "kiosk_clock",
    },
    { merge: true }
  );

  batch.set(
    openRef,
    {
      clockInAt: Timestamp.fromDate(nextStart),
      clockOutAt: Timestamp.fromDate(nextEnd),
      status: "closed",
      payrollRecordIds: nextPayrollIds,
      segmentCount: segments.length,
      totalHours,
      totalPay,
      updatedAt: serverTimestamp(),
      editedByUid: editorUid || null,
      editedByEmail: editorEmail || null,
    },
    { merge: true }
  );

  batch.delete(legacyRef);

  batch.set(doc(collection(db, CLOCK_EVENTS_COL)), {
    uid,
    type: "clock_adjusted",
    source: "admin_secret_clock_edit",
    targetClockEventId: eventId,
    previousPayrollRecordIds: payrollRecordIds,
    nextPayrollRecordIds: nextPayrollIds,
    adjustedClockInAt: Timestamp.fromDate(nextStart),
    adjustedClockOutAt: Timestamp.fromDate(nextEnd),
    editedByUid: editorUid || null,
    editedByEmail: editorEmail || null,
    createdAt: serverTimestamp(),
  });

  await batch.commit();

  return {
    adjustedSegments: segments.length,
    adjustedPayrollRecordIds: nextPayrollIds,
  };
}

export async function upsertAvailabilityByDate(dateObj, tutorUid, data = {}) {
  if (!dateObj || !tutorUid) return null;

  const base = new Date(dateObj);
  base.setHours(0, 0, 0, 0);

  const startMinRaw = Number(data?.startMin);
  const endMinRaw = Number(data?.endMin);
  const fallbackStart = 14 * 60;
  const startMin = Number.isFinite(startMinRaw) ? Math.max(0, Math.min(23 * 60 + 55, startMinRaw)) : fallbackStart;
  const endMin =
    Number.isFinite(endMinRaw) && endMinRaw > startMin
      ? Math.min(24 * 60, endMinRaw)
      : Math.min(24 * 60, startMin + 60);

  const start = new Date(base);
  start.setMinutes(startMin);

  const end = new Date(base);
  end.setMinutes(endMin);

  const docId = `day_${dateKeyLocal(base)}_${tutorUid}`;
  const ref = doc(db, AVAIL_COL, docId);

  await setDoc(
    ref,
    {
      uid: tutorUid,
      start: Timestamp.fromDate(start),
      end: Timestamp.fromDate(end),
      startMin,
      endMin,
      unavailable: !!data?.unavailable,
      hidden: !!data?.hidden,
      source: "schedule_day_settings",
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );

  return docId;
}

// ---------------- MASTER SCHEDULE (TEMPLATE) SESSIONS ----------------

export async function fetchMasterScheduleSessions({ tutorUid = null, includeDeleted = false } = {}) {
  let qRef = collection(db, MASTER_SCHEDULE_SESSIONS_COL);
  if (tutorUid) {
    qRef = query(qRef, where("tutorUid", "==", tutorUid));
  }
  const snap = await getDocs(qRef);
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    start: toDateMaybe(d.data().start),
    end: toDateMaybe(d.data().end),
  }));

  if (includeDeleted) return rows;
  return rows.filter((s) => !s?.isDeleted);
}

export async function createMasterScheduleSession(payload) {
  const docRef = await addDoc(collection(db, MASTER_SCHEDULE_SESSIONS_COL), {
    ...payload,
    isDeleted: false,
    deletedAt: null,
    deletedByUid: null,
    deletedByName: null,
    deletedReason: null,
  });

  // LOGGING (Master)
  const context = formatLogContext(payload.dayKey, payload.start, payload.end);
  await logSchedulingActivity("CREATE_MASTER", "Created master template", {
    studentName: payload.studentName,
    tutorName: payload.tutorName,
    sessionContext: `(Master) ${context}`,
    valFrom: "-",
    valTo: "Active",
    adminName: await fetchAdminName()
  });

  return docRef.id;
}

export async function updateMasterScheduleSession(id, patch) {
  const ref = doc(db, MASTER_SCHEDULE_SESSIONS_COL, id);
  const snap = await getDoc(ref);
  const oldData = snap.exists() ? snap.data() : {};
  const oldStart = toDateMaybe(oldData.start);
  const oldEnd = toDateMaybe(oldData.end);

  await updateDoc(ref, patch);

  const patchKeys = Object.keys(patch || {});
  const meaningfulKeys = patchKeys.filter((k) => k !== "visualOrder" && k !== "updatedAt");
  if (meaningfulKeys.length === 0) {
    return;
  }

  // LOGGING (Simple diff for master)
  let action = "UPDATE_MASTER";
  let valFrom = "-";
  let valTo = "-";
  
  if (patch.status) {
    valFrom = oldData.status; 
    valTo = patch.status;
  } else if (patch.start) {
    action = "MOVE_MASTER";
    valFrom = formatRange(oldStart, oldEnd);
    valTo = formatRange(patch.start, patch.end);
  }

  const context = formatLogContext(oldData.dayKey, oldStart, oldEnd);
  await logSchedulingActivity(action, "Updated master session", {
    studentName: oldData.studentName,
    tutorName: oldData.tutorName,
    sessionContext: `(Master) ${context}`,
    valFrom,
    valTo,
    adminName: await fetchAdminName()
  });
}

export async function deleteMasterScheduleSession(id, meta = {}) {
  const ref = doc(db, MASTER_SCHEDULE_SESSIONS_COL, id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};

  // CAPTURE ADMIN NAME
  const adminName = meta.deletedByName || await fetchAdminName();

  await updateDoc(ref, {
    isDeleted: true,
    deletedAt: Timestamp.now(),
    deletedByUid: meta.deletedByUid || null,
    deletedByName: adminName, // <--- SAVING NAME HERE
    deletedReason: meta.deletedReason || null,
    updatedAt: Timestamp.now(),
  });

  const context = formatLogContext(data.dayKey, data.start, data.end);
  await logSchedulingActivity("DELETE_MASTER", "Soft deleted master template", {
    studentName: data.studentName || "Unknown",
    sessionContext: `(Master) ${context}`,
    valFrom: "Active",
    valTo: "Deleted",
    adminName: adminName // <--- LOGGING NAME HERE
  });
}

export async function permanentlyDeleteMasterSession(id) {
  await deleteDoc(doc(db, MASTER_SCHEDULE_SESSIONS_COL, id));
}

export async function batchPermanentlyDeleteMasterSessions(ids) {
  if (!ids || ids.length === 0) return;
  const batch = writeBatch(db);
  ids.forEach(id => {
    batch.delete(doc(db, MASTER_SCHEDULE_SESSIONS_COL, id));
  });
  await batch.commit();
}

export async function restoreMasterScheduleSession(id) {
  const ref = doc(db, MASTER_SCHEDULE_SESSIONS_COL, id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};

  await updateDoc(ref, {
    isDeleted: false,
    deletedAt: null,
    deletedByUid: null,
    deletedByName: null,
    deletedReason: null,
    updatedAt: Timestamp.now(),
  });

  const context = formatLogContext(data.dayKey, data.start, data.end);
  await logSchedulingActivity("RESTORE_MASTER", "Restored master template", {
    studentName: data.studentName,
    sessionContext: `(Master) ${context}`,
    valFrom: "Deleted",
    valTo: "Active",
    adminName: await fetchAdminName()
  });
}

// ---------------- SCHEDULED SESSIONS (LIVE) ----------------

export async function createScheduledSession(payload) {
  // 1. Database Write (Create the session)
  const ref = await addDoc(collection(db, SESSIONS_COL), {
    ...payload,
    isDeleted: false,
    deletedAt: null,
    deletedByUid: null,
    deletedByName: null,
    deletedReason: null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  
  // Only log non-pending session creation — pending sessions are tentative
  if (normalizeSessionStatus(payload.status) !== "pending") {
    try {
      const startDate = toDateMaybe(payload.start) || (payload.start ? new Date(payload.start) : null);
      const endDate = toDateMaybe(payload.end) || (payload.end ? new Date(payload.end) : null);
      const context = formatLogContext(payload.dayKey, startDate, endDate) || "Date Context Missing";
      const finalTutorName = payload.tutorName || "Unassigned";

      await logSchedulingActivity("CREATE_SESSION", "Created new session", {
        studentName: payload.studentName || "Unknown Student",
        tutorName: finalTutorName,
        sessionContext: context,
        dateKey: payload.dayKey || "",
        valFrom: "-",
        valTo: payload.status || "Pending",
        adminName: await fetchAdminName()
      });
    } catch (err) {
      console.error("Failed to log create session:", err);
    }
  }

  return ref.id;
}

export async function updateScheduledSession(sessionId, patch) {
  const ref = doc(db, SESSIONS_COL, sessionId);
  
  // 1. Fetch OLD data
  const snap = await getDoc(ref);
  const oldData = snap.exists() ? snap.data() : {};
  const oldStart = toDateMaybe(oldData.start);
  const oldEnd = toDateMaybe(oldData.end);
  const hasStatusInPatch = hasOwn(patch, "status");
  const prevStatusKey = normalizeSessionStatus(oldData.status);
  const nextStatusKey = normalizeSessionStatus(hasStatusInPatch ? patch.status : oldData.status);

  let noShowHoursDelta = 0;
  let computedNoShowHours = Number(oldData?.noShowAppliedHours || 0);
  if (!Number.isFinite(computedNoShowHours) || computedNoShowHours < 0) computedNoShowHours = 0;

  const patchToWrite = { ...patch };
  if (hasStatusInPatch) {
    if (prevStatusKey !== "no_show" && nextStatusKey === "no_show") {
      computedNoShowHours = calculateRoundedUsedHours(oldData.start, oldData.end);
      if (computedNoShowHours > 0) {
        noShowHoursDelta = computedNoShowHours;
      }
      patchToWrite.noShowAppliedHours = computedNoShowHours;
      patchToWrite.noShowAppliedAt = Timestamp.now();
    } else if (prevStatusKey === "no_show" && nextStatusKey !== "no_show") {
      if (computedNoShowHours > 0) {
        noShowHoursDelta = -computedNoShowHours;
      }
      patchToWrite.noShowAppliedHours = 0;
      patchToWrite.noShowAppliedAt = null;
    }
  }

  // 2. Perform Update
  await updateDoc(ref, { ...patchToWrite, updatedAt: Timestamp.now() });

  if (Math.abs(noShowHoursDelta) > 0.0001 && oldData?.studentId) {
    const studentRef = doc(db, STUDENTS_COL, String(oldData.studentId));
    await updateDoc(studentRef, {
      totalSessionHours: increment(noShowHoursDelta),
      totalSessionHoursUpdatedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  // 3. GENERATE "PERFECT" DIFF LOG
  const changes = [];
  let action = "UPDATE_SESSION";
  let tutorDisplay = oldData.tutorName || "Unknown";
  
  // A. Tutor Change
  if (patchToWrite.tutorUid && patchToWrite.tutorUid !== oldData.tutorUid) {
    const oldT = oldData.tutorName || "Unknown";
    const newT = patchToWrite.tutorName || "New Tutor";
    tutorDisplay = `${oldT} → ${newT}`;
    changes.push(`Tutor: ${oldT} → ${newT}`);
    action = "TUTOR_CHANGE";
  }

  // B. Time Change
  const newStart = patchToWrite.start ? toDateMaybe(patchToWrite.start) : oldStart;
  const newEnd = patchToWrite.end ? toDateMaybe(patchToWrite.end) : oldEnd;
  const timeChanged = (newStart?.getTime() !== oldStart?.getTime()) || (newEnd?.getTime() !== oldEnd?.getTime());
  
  if (timeChanged) {
    const oldTime = formatRange(oldStart, oldEnd);
    const newTime = formatRange(newStart, newEnd);
    
    // Check Date Change too
    const oldDay = oldData.dayKey;
    const newDay = patchToWrite.dayKey || oldDay;
    
    if (oldDay !== newDay) {
      changes.push(`Day: ${oldDay} → ${newDay}`);
    }
    if (oldTime !== newTime) {
      changes.push(`Time: ${oldTime} → ${newTime}`);
    }
    action = "MOVE_SESSION";
  }

  // C. Status Change
  if (patchToWrite.status && patchToWrite.status !== oldData.status) {
    changes.push(`Status: ${oldData.status} → ${patchToWrite.status}`);
    action = "STATUS_CHANGE";
  }

  // D. Subject Change
  if (patchToWrite.subject !== undefined && patchToWrite.subject !== oldData.subject) {
    const oldSubj = oldData.subject || "(none)";
    const newSubj = patchToWrite.subject || "(none)";
    changes.push(`Subject: ${oldSubj} → ${newSubj}`);
    action = "SUBJECT_CHANGE";
  }

  // E. Note Change
  if (patchToWrite.note !== undefined && patchToWrite.note !== oldData.note) {
    changes.push(`Note: ${oldData.note || "(none)"} → ${patchToWrite.note}`);
    action = "NOTE_CHANGE";
  }

  const patchKeys = Object.keys(patchToWrite || {});
  const meaningfulKeys = patchKeys.filter((k) => k !== "visualOrder" && k !== "updatedAt");
  if (meaningfulKeys.length === 0 || changes.length === 0) {
    return;
  }

  // Determine Final From/To Strings
  let valFrom = "-";
  let valTo = "-";

  if (changes.length === 1) {
    // Single change: Extract "X -> Y"
    const parts = changes[0].split(" → ");
    if (parts.length === 2) {
      const labelParts = parts[0].split(": ");
      valFrom = labelParts[1] || parts[0];
      valTo = parts[1];
    } else {
      valFrom = "Old Value";
      valTo = "New Value";
    }
  } else if (changes.length > 1) {
    // Multi change: Use Multi-line format
    action = "MULTI_UPDATE";
    valFrom = changes.map(c => c.split(" → ")[0]).join("\n");
    valTo = changes.map(c => c.split(" → ")[1]).join("\n");
  }

  // Skip log when the session is pending before AND after — tentative changes are noise
  if (prevStatusKey === "pending" && nextStatusKey === "pending") {
    return;
  }

  // Context is ALWAYS the OLD session time (so we know what was changed)
  const context = formatLogContext(oldData.dayKey, oldStart, oldEnd);

  await logSchedulingActivity(action, `Updated session`, {
    studentName: oldData.studentName,
    tutorName: tutorDisplay,
    sessionContext: context,
    dateKey: oldData.dayKey,
    valFrom,
    valTo,
    adminName: await fetchAdminName()
  });
}

export async function deleteScheduledSession(sessionId, meta = {}) {
  const {
    deletedByUid = null,
    deletedReason = null,
  } = meta || {};

  const ref = doc(db, SESSIONS_COL, sessionId);
  
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};

  // CAPTURE ADMIN NAME
  const adminName = meta.deletedByName || await fetchAdminName();

  await updateDoc(ref, {
    isDeleted: true,
    deletedAt: Timestamp.now(),
    deletedByUid,
    deletedByName: adminName, // <--- SAVING NAME HERE
    deletedReason,
    updatedAt: Timestamp.now(),
  });

  // Don't log deletion of pending sessions — they were never confirmed
  if (normalizeSessionStatus(data.status) !== "pending") {
    const context = formatLogContext(data.dayKey, data.start, data.end);
    await logSchedulingActivity("DELETE_SESSION", "Soft deleted session", {
      studentName: data.studentName || "Unknown",
      tutorName: data.tutorName,
      sessionContext: context,
      valFrom: "Active",
      valTo: "Deleted",
      adminName: adminName
    });
  }
}

export async function permanentlyDeleteScheduledSession(id) {
  const ref = doc(db, SESSIONS_COL, id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  
  await deleteDoc(ref);

  await logSchedulingActivity("PERMANENT_DELETE", "Permanently wiped session", { 
    studentName: data.studentName || "Unknown",
    valFrom: "Trash",
    valTo: "Wiped",
    adminName: await fetchAdminName()
  });
}

export async function batchPermanentlyDeleteScheduledSessions(ids) {
  const batch = writeBatch(db);
  ids.forEach((id) => {
    const ref = doc(db, SESSIONS_COL, id);
    batch.delete(ref);
  });
  await batch.commit();

  // --- CORRECTED LOGGING ---
  if (ids.length > 0) {
    try {
      // Must pass 3 separate arguments: Type, Title, Metadata Object
      await logSchedulingActivity(
        "BATCH_PERMANENT_DELETE",               // 1. Action Type (triggers the badge)
        "Emptied Trash (Batch Wipe)",           // 2. Action Title/Description
        {                                       // 3. Metadata
          studentName: `${ids.length} Sessions`,
          tutorName: "Various",
          sessionContext: "Trash Empty",
          valFrom: "Trash",
          valTo: "Wiped",
          adminName: await fetchAdminName()
        }
      );
    } catch (e) {
      console.error("Failed to log batch delete:", e);
    }
  }
}

export async function restoreScheduledSession(sessionId) {
  const ref = doc(db, SESSIONS_COL, sessionId);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};

  await updateDoc(ref, {
    isDeleted: false,
    deletedAt: null,
    deletedByUid: null,
    deletedByName: null,
    deletedReason: null,
    updatedAt: Timestamp.now(),
  });

  // Don't log restoration of pending sessions — they were never confirmed
  if (normalizeSessionStatus(data.status) !== "pending") {
    const context = formatLogContext(data.dayKey, data.start, data.end);
    await logSchedulingActivity("RESTORE_SESSION", "Restored from trash", {
      studentName: data.studentName,
      tutorName: data.tutorName,
      sessionContext: context,
      valFrom: "Deleted",
      valTo: "Active",
      adminName: await fetchAdminName()
    });
  }
}

export async function updateSessionStatus(sessionId, status) {
  // Re-use updateScheduledSession logic for consistency
  await updateScheduledSession(sessionId, { status });
}

export async function fetchStudentUpcomingSessions(studentId, max = 10) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  
  const startTs = Timestamp.fromDate(startOfToday);

  const q = query(
    collection(db, SESSIONS_COL),
    where("studentId", "==", studentId),
    where("start", ">=", startTs),
    orderBy("start", "asc"),
    limit(max)
  );

  const snap = await getDocs(q);
  
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    start: toDateMaybe(d.data().start),
    end: toDateMaybe(d.data().end),
  }));

  // FIX: Filter out sessions that are in the trash (soft-deleted)
  return rows.filter((s) => !s.isDeleted);
}

function dateKeyLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

export async function fetchDayConfigsForDates(dates) {
  const out = {};
  await Promise.all(
    dates.map(async (dt) => {
      const key = dateKeyLocal(dt);
      const ref = doc(db, DAYCFG_COL, key);
      const snap = await getDoc(ref);
      if (snap.exists()) out[key] = { id: key, ...snap.data() };
    })
  );
  return out;
}

export async function upsertDayConfigByDate(dateObj, patch) {
  const key = dateKeyLocal(dateObj);
  const ref = doc(db, DAYCFG_COL, key);

  await setDoc(
    ref,
    {
      dateKey: key,
      ...patch,
      updatedAt: Timestamp.now(),
      createdAt: patch?.createdAt || Timestamp.now(),
    },
    { merge: true }
  );

  return key;
}

function dayKeyFromDate(dateObj) {
  const dow = dateObj.getDay();
  const keys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return keys[dow] || null;
}

export async function fetchScheduleTypes() {
  const snap = await getDocs(collection(db, SCHEDULE_TYPES_COL));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((row) => !row?.isDeleted)
    .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
}

export async function createScheduleType(input = {}) {
  const base = buildRegularScheduleTypeDraft();
  const normalizedWeekConfig = normalizeWeekConfig(input?.weekConfig || base.weekConfig);
  const payload = {
    name: String(input?.name || base.name).trim() || base.name,
    description: String(input?.description || "").trim(),
    weekConfig: normalizedWeekConfig,
    revision: 1,
    isDeleted: false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  const ref = await addDoc(collection(db, SCHEDULE_TYPES_COL), payload);
  return ref.id;
}

export async function updateScheduleType(typeId, patch = {}) {
  if (!typeId) throw new Error("schedule type id is required");
  const ref = doc(db, SCHEDULE_TYPES_COL, typeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("schedule type not found");

  const prev = snap.data() || {};
  const nextRevision = Number(prev?.revision || 1) + 1;
  const nextWeekConfig = patch?.weekConfig
    ? normalizeWeekConfig(patch.weekConfig, normalizeWeekConfig(prev?.weekConfig))
    : prev?.weekConfig;

  const next = {
    updatedAt: Timestamp.now(),
    revision: nextRevision,
  };
  if (typeof patch?.name === "string") next.name = patch.name.trim();
  if (typeof patch?.description === "string") next.description = patch.description.trim();
  if (nextWeekConfig) next.weekConfig = nextWeekConfig;

  await updateDoc(ref, next);
  return true;
}

export async function deleteScheduleType(typeId) {
  if (!typeId) throw new Error("schedule type id is required");
  const ref = doc(db, SCHEDULE_TYPES_COL, typeId);
  await setDoc(
    ref,
    {
      isDeleted: true,
      deletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );
  return true;
}

export async function fetchScheduleApplications({ limitCount = 200 } = {}) {
  let qRef = query(collection(db, SCHEDULE_APPLICATIONS_COL), orderBy("startDate", "desc"));
  if (Number.isFinite(limitCount) && limitCount > 0) {
    qRef = query(qRef, limit(limitCount));
  }
  const snap = await getDocs(qRef);
  return snap.docs.map((d) => {
    const data = d.data() || {};
    return {
      id: d.id,
      ...data,
      startDate: toDateMaybe(data.startDate),
      endDate: toDateMaybe(data.endDate),
      createdAt: toDateMaybe(data.createdAt),
      updatedAt: toDateMaybe(data.updatedAt),
    };
  });
}

export async function applyScheduleTypeToDateRange({
  typeId,
  startDate,
  endDate,
}) {
  if (!typeId) throw new Error("schedule type id is required");
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    throw new Error("start and end dates are required");
  }

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  if (end < start) throw new Error("end date must be on or after start date");

  const typeRef = doc(db, SCHEDULE_TYPES_COL, typeId);
  const typeSnap = await getDoc(typeRef);
  if (!typeSnap.exists()) throw new Error("schedule type not found");
  const type = typeSnap.data() || {};
  if (type?.isDeleted) throw new Error("cannot apply a deleted schedule type");

  const normalizedWeekConfig = normalizeWeekConfig(type?.weekConfig);
  const appRef = doc(collection(db, SCHEDULE_APPLICATIONS_COL));
  const createdAt = Timestamp.now();

  const applicationPayload = {
    typeId,
    typeName: type?.name || "Schedule",
    typeDescription: type?.description || "",
    typeRevision: Number(type?.revision || 1),
    weekConfigSnapshot: normalizedWeekConfig,
    startDate: Timestamp.fromDate(start),
    endDate: Timestamp.fromDate(end),
    startDateKey: dateKeyLocal(start),
    endDateKey: dateKeyLocal(end),
    createdAt,
    updatedAt: createdAt,
  };
  await setDoc(appRef, applicationPayload);

  const allDates = enumerateDatesInclusive(start, end);
  const dateChunks = chunkArray(allDates, 400);

  for (const dateChunk of dateChunks) {
    const batch = writeBatch(db);
    dateChunk.forEach((dateObj) => {
      const dayKey = dayKeyFromDate(dateObj);
      const rule = normalizeDaySchedule(normalizedWeekConfig?.[dayKey], normalizedWeekConfig?.[dayKey]);
      const docId = dateKeyLocal(dateObj);
      const ref = doc(db, DAYCFG_COL, docId);

      batch.set(
        ref,
        {
          dateKey: docId,
          isClosed: !rule?.isOpen,
          timelineStartMin: Number(rule?.startMin ?? 14 * 60),
          timelineEndMin: Number(rule?.endMin ?? 21 * 60),
          appliedScheduleTypeId: typeId,
          appliedScheduleTypeName: type?.name || "Schedule",
          appliedScheduleTypeRevision: Number(type?.revision || 1),
          scheduleApplicationId: appRef.id,
          scheduleApplicationRange: {
            startDateKey: dateKeyLocal(start),
            endDateKey: dateKeyLocal(end),
          },
          updatedAt: createdAt,
          createdAt,
        },
        { merge: true }
      );
    });
    await batch.commit();
  }

  return { id: appRef.id, appliedDays: allDates.length };
}

export async function ensureDefaultRegularScheduleType() {
  const existing = await fetchScheduleTypes();
  if (existing.length > 0) {
    return existing[0].id;
  }
  const draft = buildRegularScheduleTypeDraft();
  return createScheduleType(draft);
}

// ── Default weekly schedule (settings/scheduleDefault) ────────────────────────
// The editable default applied to every date that has no explicit day-config.
// Absent doc (or a read denied by rules for non-admins) → LEGACY default, i.e.
// identical to the previous hardcoded behavior.

const DEFAULT_SCHEDULE_DOC_ID = "scheduleDefault";

export async function fetchDefaultScheduleWeekConfig() {
  try {
    const ref = doc(db, SETTINGS_COL, DEFAULT_SCHEDULE_DOC_ID);
    const snap = await getDoc(ref);
    if (!snap.exists()) return normalizeWeekConfig(LEGACY_REGULAR_WEEK_CONFIG);
    const data = snap.data() || {};
    return normalizeWeekConfig(data.weekConfig, LEGACY_REGULAR_WEEK_CONFIG);
  } catch (error) {
    // Non-admins cannot read settings/*; fall back to the built-in default.
    console.warn("Failed to load default schedule; using built-in default.", error);
    return normalizeWeekConfig(LEGACY_REGULAR_WEEK_CONFIG);
  }
}

export async function saveDefaultScheduleWeekConfig(weekConfig) {
  const normalized = normalizeWeekConfig(weekConfig, LEGACY_REGULAR_WEEK_CONFIG);
  const ref = doc(db, SETTINGS_COL, DEFAULT_SCHEDULE_DOC_ID);
  await setDoc(
    ref,
    { weekConfig: normalized, updatedAt: Timestamp.now() },
    { merge: true }
  );
  return normalized;
}

// Apply a raw weekly config to a date range by writing per-day day-configs.
// Same mechanism as applyScheduleTypeToDateRange, minus the named type. Also
// records a lightweight schedule_applications history entry for audit parity.
export async function applyWeekConfigToDateRange({ weekConfig, startDate, endDate }) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    throw new Error("start and end dates are required");
  }

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  if (end < start) throw new Error("end date must be on or after start date");

  const normalizedWeekConfig = normalizeWeekConfig(weekConfig, LEGACY_REGULAR_WEEK_CONFIG);
  const appRef = doc(collection(db, SCHEDULE_APPLICATIONS_COL));
  const createdAt = Timestamp.now();

  await setDoc(appRef, {
    typeId: null,
    typeName: "Custom Schedule",
    typeDescription: "",
    typeRevision: 1,
    weekConfigSnapshot: normalizedWeekConfig,
    startDate: Timestamp.fromDate(start),
    endDate: Timestamp.fromDate(end),
    startDateKey: dateKeyLocal(start),
    endDateKey: dateKeyLocal(end),
    source: "range_apply",
    createdAt,
    updatedAt: createdAt,
  });

  const allDates = enumerateDatesInclusive(start, end);
  const dateChunks = chunkArray(allDates, 400);

  for (const dateChunk of dateChunks) {
    const batch = writeBatch(db);
    dateChunk.forEach((dateObj) => {
      const dayKey = dayKeyFromDate(dateObj);
      const rule = normalizeDaySchedule(normalizedWeekConfig?.[dayKey], normalizedWeekConfig?.[dayKey]);
      const docId = dateKeyLocal(dateObj);
      const ref = doc(db, DAYCFG_COL, docId);

      batch.set(
        ref,
        {
          dateKey: docId,
          isClosed: !rule?.isOpen,
          timelineStartMin: Number(rule?.startMin ?? 14 * 60),
          timelineEndMin: Number(rule?.endMin ?? 21 * 60),
          source: "range_apply",
          scheduleApplicationId: appRef.id,
          scheduleApplicationRange: {
            startDateKey: dateKeyLocal(start),
            endDateKey: dateKeyLocal(end),
          },
          updatedAt: createdAt,
          createdAt,
        },
        { merge: true }
      );
    });
    await batch.commit();
  }

  return { id: appRef.id, appliedDays: allDates.length };
}

export async function batchDeleteSessionsByIds(sessionIds) {
  if (!sessionIds?.length) return;

  // Snapshot the sessions BEFORE deleting so the activity log can record every
  // affected student and their session time. Without this a multi-delete only
  // wrote an anonymous "N Students / Multiple Sessions" entry.
  const snaps = await Promise.all(
    sessionIds.map((id) => getDoc(doc(db, SESSIONS_COL, id)))
  );
  const detailLines = snaps
    .filter((snap) => snap.exists())
    .map((snap) => snap.data())
    .map((data) => {
      const name = data.studentName || "Unknown";
      const context = formatLogContext(data.dayKey, data.start, data.end);
      return `${name} — ${context}`;
    });

  const batch = writeBatch(db);
  sessionIds.forEach((id) =>
    batch.update(doc(db, SESSIONS_COL, id), {
      isDeleted: true,
      deletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    })
  );
  await batch.commit();

  await logSchedulingActivity("BATCH_DELETE", `Batch deleted ${sessionIds.length} sessions`, {
    studentName: `${sessionIds.length} Students`,
    tutorName: "Various",
    sessionContext: detailLines.join("\n") || "Multiple Sessions",
    valFrom: "Active",
    valTo: "Deleted",
    adminName: await fetchAdminName()
  });
}

export async function batchUpdateSessions(patches) {
  if (!patches?.length) return;

  const batch = writeBatch(db);
  
  // Fetch first session to get context
  const firstId = patches[0].id;
  const firstRef = doc(db, SESSIONS_COL, firstId);
  const firstSnap = await getDoc(firstRef);
  const firstData = firstSnap.exists() ? firstSnap.data() : {};
  
  const context = formatLogContext(firstData.dayKey, firstData.start, firstData.end);
  const contextText = patches.length > 1 ? `${context} (+${patches.length-1} others)` : context;

  for (const { id, patch } of patches) {
    batch.update(doc(db, "scheduled_sessions", id), { ...patch, updatedAt: new Date() });
  }
  await batch.commit();

  // Log
  const keys = Object.keys(patches[0].patch);
  let action = "BATCH_UPDATE";
  let valFrom = "Various";
  let valTo = "Updated";

  if (keys.includes("status")) {
    action = "BATCH_STATUS";
    valTo = patches[0].patch.status;
  } else if (keys.includes("start")) {
    action = "BATCH_MOVE";
    valTo = "New Time Block";
  }

  await logSchedulingActivity(action, `Batch update of ${patches.length} sessions`, {
    studentName: `${patches.length} Students`,
    tutorName: "Various",
    sessionContext: contextText,
    valFrom,
    valTo,
    adminName: await fetchAdminName()
  });
}

export async function fetchTutorOrder() {
  const ref = doc(db, SETTINGS_COL, "global_tutor_order");
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return snap.data().order || [];
  }
  return [];
}

export async function saveTutorOrder(orderIds) {
  const ref = doc(db, SETTINGS_COL, "global_tutor_order");
  await setDoc(ref, { order: orderIds, updatedAt: Timestamp.now() }, { merge: true });
}

export async function fetchSubjects() {
  const q = query(collection(db, SUBJECTS_COL), orderBy("name", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, name: d.data().name }));
}

export async function createSubject(name) {
  const q = query(collection(db, SUBJECTS_COL), where("name", "==", name));
  const snap = await getDocs(q);
  
  if (!snap.empty) {
    return snap.docs[0].id; 
  }

  const ref = await addDoc(collection(db, SUBJECTS_COL), {
    name,
    createdAt: Timestamp.now(),
  });
  return ref.id;
}

export async function duplicateScheduleRangeToTargetWeek(
  sourceStart,
  sourceEnd,
  targetWeekStart,
  selectedDayKeys
) {
  if (!(sourceStart instanceof Date) || !(sourceEnd instanceof Date) || !(targetWeekStart instanceof Date)) {
    throw new Error("Invalid duplicate date range.");
  }

  const sourceRangeStart = new Date(sourceStart);
  sourceRangeStart.setHours(0, 0, 0, 0);
  const sourceRangeEnd = new Date(sourceEnd);
  sourceRangeEnd.setHours(0, 0, 0, 0);
  if (sourceRangeEnd <= sourceRangeStart) return 0;

  const targetRangeStart = new Date(targetWeekStart);
  targetRangeStart.setHours(0, 0, 0, 0);
  const targetRangeEnd = new Date(targetRangeStart);
  targetRangeEnd.setDate(targetRangeEnd.getDate() + 7);

  const students = await fetchAllStudents();

  const sourceSessions = await fetchScheduledSessions({
    rangeStart: sourceRangeStart,
    rangeEnd: sourceRangeEnd,
    includeDeleted: false,
  });
  if (sourceSessions.length === 0) return 0;

  const existingTargetSessions = await fetchScheduledSessions({
    rangeStart: targetRangeStart,
    rangeEnd: targetRangeEnd,
    includeDeleted: false,
  });

  const daysFilter = Array.isArray(selectedDayKeys) && selectedDayKeys.length ? selectedDayKeys : null;
  const sessionsToDelete = existingTargetSessions.filter((s) => {
    if (!daysFilter) return true;
    return s.dayKey && daysFilter.includes(s.dayKey);
  });
  if (sessionsToDelete.length > 0) {
    await batchDeleteSessionsByIds(sessionsToDelete.map((s) => s.id));
  }

  const validSessionsToProcess = [];
  const uniqueTargetDatesMap = new Map();

  for (const session of sourceSessions) {
    if (!session?.start || !session?.end) continue;

    const status = String(session.status || "").toLowerCase();
    if (status === "cancelled" || status === "canceled") continue;

    const sourceDayKey = session.dayKey || dayKeyFromDate(new Date(session.start));
    if (daysFilter && (!sourceDayKey || !daysFilter.includes(sourceDayKey))) continue;

    const sourceSessionDate = new Date(session.start);
    sourceSessionDate.setHours(0, 0, 0, 0);
    const dayOffset = Math.round((sourceSessionDate.getTime() - sourceRangeStart.getTime()) / 86400000);

    const targetDate = new Date(targetRangeStart);
    targetDate.setDate(targetDate.getDate() + dayOffset);

    const sourceStartAt = new Date(session.start);
    const sourceEndAt = new Date(session.end);

    const newStart = new Date(targetDate);
    newStart.setHours(sourceStartAt.getHours(), sourceStartAt.getMinutes(), 0, 0);

    const newEnd = new Date(targetDate);
    newEnd.setHours(sourceEndAt.getHours(), sourceEndAt.getMinutes(), 0, 0);
    if (newEnd <= newStart) newEnd.setDate(newEnd.getDate() + 1);

    const targetDateKey = dateKeyLocal(newStart);
    if (!uniqueTargetDatesMap.has(targetDateKey)) {
      uniqueTargetDatesMap.set(targetDateKey, newStart);
    }

    validSessionsToProcess.push({
      original: session,
      newStart,
      newEnd,
      dayKey: sourceDayKey,
      targetDateKey,
    });
  }

  if (validSessionsToProcess.length === 0) return 0;

  const targetDatesList = Array.from(uniqueTargetDatesMap.values());
  const dayConfigs = await fetchDayConfigsForDates(targetDatesList);

  const batch = writeBatch(db);
  let count = 0;

  validSessionsToProcess.forEach((item) => {
    const { original, newStart, newEnd, dayKey, targetDateKey } = item;
    const config = dayConfigs[targetDateKey];
    if (config?.isClosed) return;

    const newRef = doc(collection(db, SESSIONS_COL));
    const studentGrade = resolveCopiedStudentGrade(students, original);
    batch.set(newRef, {
      tutorUid: original.tutorUid,
      tutorName: original.tutorName || "",
      studentId: original.studentId,
      studentName: original.studentName || "Unknown",
      studentGrade,
      subject: original.subject || "",
      note: original.note || "",
      status: "Pending",
      start: Timestamp.fromDate(newStart),
      end: Timestamp.fromDate(newEnd),
      dayKey,
      blockId: original.blockId || "",
      slotIdx: original.slotIdx ?? 0,
      visualOrder: Number.isFinite(Number(original.visualOrder)) ? Number(original.visualOrder) : 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    count += 1;
  });

  if (count > 0) {
    await batch.commit();
    await logSchedulingActivity("DUPLICATE_WEEK", `Duplicated ${count} sessions`, {
      studentName: `${count} Sessions`,
      sessionContext: `${sourceRangeStart.toLocaleDateString()} → ${targetRangeStart.toLocaleDateString()}`,
      valFrom: "-",
      valTo: "Pending",
      adminName: await fetchAdminName(),
    });
  }

  return count;
}

export async function duplicateWeekSessions(sourceStart, sourceEnd, selectedDayKeys) {
  const targetWeekStart = new Date(sourceStart);
  targetWeekStart.setHours(0, 0, 0, 0);
  targetWeekStart.setDate(targetWeekStart.getDate() + 7);
  return duplicateScheduleRangeToTargetWeek(sourceStart, sourceEnd, targetWeekStart, selectedDayKeys);
}

export async function duplicateMasterScheduleToWeek(targetWeekStart, selectedDayKeys) {
  // 1. Clean Up Existing Sessions in Target Week First
  const targetStart = new Date(targetWeekStart);
  const targetEnd = new Date(targetWeekStart);
  targetEnd.setDate(targetEnd.getDate() + 6);
  const targetEndExclusive = new Date(targetEnd);
  targetEndExclusive.setDate(targetEndExclusive.getDate() + 1);

  // Fetch what currently exists in the target week
  const existingTargetSessions = await fetchScheduledSessions({ 
    rangeStart: targetStart, 
    rangeEnd: targetEndExclusive,
    includeDeleted: false 
  });

  // Filter existing sessions to only those falling on the days we are about to write
  const sessionsToDelete = existingTargetSessions.filter(s => {
    if (!selectedDayKeys || !Array.isArray(selectedDayKeys)) return true;
    return s.dayKey && selectedDayKeys.includes(s.dayKey);
  });

  // Wipe them out
  if (sessionsToDelete.length > 0) {
    await batchDeleteSessionsByIds(sessionsToDelete.map(s => s.id));
  }

  // 2. Fetch Master Sessions & Proceed (Existing Logic)
  const students = await fetchAllStudents();
  const masterSessions = await fetchMasterScheduleSessions();

  const dayOffset = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
  };

  const dateObjs = [];
  for (let d = new Date(targetStart); d <= targetEnd; d.setDate(d.getDate() + 1)) {
    dateObjs.push(new Date(d)); 
  }
  const dayConfigsMap = await fetchDayConfigsForDates(dateObjs);

  const validSessionsToProcess = masterSessions.filter((s) => s.status !== "Cancelled");

  const batch = writeBatch(db);
  let count = 0;

  for (const sess of validSessionsToProcess) {
    if (selectedDayKeys && Array.isArray(selectedDayKeys)) {
      if (!selectedDayKeys.includes(sess.dayKey)) continue;
    }

    const off = dayOffset[sess.dayKey];
    if (off === undefined) continue;

    const targetDate = new Date(targetStart);
    targetDate.setDate(targetDate.getDate() + off);

    const targetKey = dateKeyLocal(targetDate);
    const cfg = dayConfigsMap[targetKey] || null;
    if (cfg?.isClosed) continue;

    const start = sess.start instanceof Date ? sess.start : new Date(sess.start);
    const end = sess.end instanceof Date ? sess.end : new Date(sess.end);
    const studentGrade = resolveCopiedStudentGrade(students, sess);

    const newStart = new Date(targetDate);
    newStart.setHours(start.getHours(), start.getMinutes(), 0, 0);

    const newEnd = new Date(targetDate);
    newEnd.setHours(end.getHours(), end.getMinutes(), 0, 0);

    const payload = {
      ...sess,
      studentGrade,
      start: Timestamp.fromDate(newStart),
      end: Timestamp.fromDate(newEnd),
      status: "Pending",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    delete payload.id;

    const docRef = doc(collection(db, SESSIONS_COL));
    batch.set(docRef, payload);
    count += 1;
  }

  if (count > 0) {
    await batch.commit();
    await logSchedulingActivity(
      "DUPLICATE_MASTER", 
      "Master Schedule Duplication",
      { 
        studentName: `${count} Sessions`,
        sessionContext: `Week of ${targetStart.toLocaleDateString()}`,
        valFrom: "Master Template",
        valTo: "Active Week (Pending)",
        adminName: await fetchAdminName()
      }
    );
  }
  return count;
}

export async function fetchAdminSchedulingUiSettings() {
  const ref = doc(db, SETTINGS_COL, "adminSchedulingUi");
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { defaultOpenView: "week", timeSlotLayout: "one" };
  }
  const data = snap.data() || {};
  return {
    defaultOpenView: data.defaultOpenView || "week",
    timeSlotLayout: data.timeSlotLayout || "one",
  };
}

export async function saveAdminSchedulingUiSettings(settings) {
  const payload = {
    defaultOpenView: settings?.defaultOpenView || "week",
    timeSlotLayout: settings?.timeSlotLayout || "one",
    updatedAt: Timestamp.now(),
  };
  const ref = doc(db, SETTINGS_COL, "adminSchedulingUi");
  await setDoc(ref, payload, { merge: true });
  return true;
}

export const MASTER_AVAIL_COL = "tutor_master_availability_blocks";

export async function fetchMasterAvailability(dayKey) {
  const q = query(
    collection(db, MASTER_AVAIL_COL),
    where("dayKey", "==", dayKey)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchAllMasterAvailability() {
  const snap = await getDocs(collection(db, MASTER_AVAIL_COL));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function upsertMasterAvailability(dayKey, tutorUid, data) {
  const docId = `master_${dayKey}_${tutorUid}`;
  const ref = doc(db, MASTER_AVAIL_COL, docId);
  await setDoc(ref, { ...data, dayKey, uid: tutorUid, updatedAt: Timestamp.now() }, { merge: true });
}

export async function fetchMasterDayConfig(dayKey) {
  const docId = `master_${dayKey}`;
  const ref = doc(db, "schedule_day_configs", docId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
}

export async function upsertMasterDayConfig(dayKey, data) {
  const docId = `master_${dayKey}`;
  const ref = doc(db, "schedule_day_configs", docId);
  await setDoc(ref, { ...data, dayKey, updatedAt: Timestamp.now() }, { merge: true });
}

export async function fetchSessionsForStudentRange(studentId, start, end) {
  // Ensure we have Date objects
  const startTs = Timestamp.fromDate(new Date(start));
  const endTs = Timestamp.fromDate(new Date(end));

  const q = query(
    collection(db, SESSIONS_COL),
    where("studentId", "==", studentId),
    where("start", ">=", startTs),
    where("start", "<", endTs),
    orderBy("start", "asc")
  );

  const snap = await getDocs(q);
  
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    start: d.data().start?.toDate(),
    end: d.data().end?.toDate(),
  }));

  // --- NEW: Filter out sessions that are in the trash ---
  return rows.filter(s => !s.isDeleted); 
}

export async function fetchTrashSessions() {
  // Query specifically for deleted items (Global Trash)
  // We limit to 300 to ensure performance, but do NOT filter by start/end date.
  const q = query(
    collection(db, SESSIONS_COL),
    where("isDeleted", "==", true),
    limit(300) 
  );

  const snap = await getDocs(q);
  
  // Map and Sort by deletedAt (descending) client-side
  return snap.docs
    .map((d) => ({
      id: d.id,
      ...d.data(),
      start: d.data().start?.toDate(),
      end: d.data().end?.toDate(),
    }))
    .sort((a, b) => {
      // Handle Firestore Timestamp or JS Date
      const tA = a.deletedAt?.toMillis ? a.deletedAt.toMillis() : (new Date(a.deletedAt || 0).getTime());
      const tB = b.deletedAt?.toMillis ? b.deletedAt.toMillis() : (new Date(b.deletedAt || 0).getTime());
      return tB - tA; // Newest first
    });
}

// ─── Real-time student session subscription ───────────────────────────────────
// Returns an unsubscribe function. Calls onData whenever Firestore data changes.
// Uses a wide date range (~3 years) to cover all practically visible months.
export function subscribeToStudentSessionsInRange(studentId, start, end, onData) {
  const startTs = Timestamp.fromDate(start instanceof Date ? start : new Date(start));
  const endTs   = Timestamp.fromDate(end   instanceof Date ? end   : new Date(end));

  const q = query(
    collection(db, SESSIONS_COL),
    where("studentId", "==", studentId),
    where("start", ">=", startTs),
    where("start", "<",  endTs),
    orderBy("start", "asc")
  );

  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
        start: d.data().start?.toDate(),
        end:   d.data().end?.toDate(),
      }))
      .filter((s) => !s.isDeleted);
    onData(rows);
  });
}
